<template>
  <WdsStack direction-column gap-custom="2.5rem" class="tw-py-2">
    <WdsStack direction-column gap-lg>
      <BaseSection>
        <BaseSectionHeader :header-text="$t('Availability')" />

        <BaseSectionBody>
          <BaseFieldGroup
            :label="$t('Start date')"
            :helper-text="$t('When are you ready to hire for this job?')"
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedStartDateTimeframe"
                :list="startDateTimeframeOptions"
                :placeholder="$t('Unknown')"
                data-test="job-start-date-availability-dropdown"
                name="job-start-date-availability-dropdown"
                :error="getErrorMessage(validationStore, 'startDateTimeframe')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            v-if="showStartDate"
            :label="$t('Specified date')"
            :helper-text="$t('Which date will this job start?')"
          >
            <div class="tw-w-full">
              <InputDatetimePicker
                v-model="selectedStartDate"
                :placeholder="$t('YYYY-MM-DD')"
                name="date_start"
                format="YYYY-MM-DD"
                :max-date="store.form.endDate"
                data-test="job-start-date"
                :error="getErrorMessage(validationStore, 'startDate')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Commitment')"
            :helper-text="$t('What level of commitment do you need?')"
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedAssociation"
                :list="associationOptions"
                :placeholder="$t('Unknown')"
                data-test="job-type-dropdown"
                name="job-type-dropdown"
                :error="getErrorMessage(validationStore, 'commitment')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Length')"
            :helper-text="$t('How long do you expect the initial contract to be?')"
            optional
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedEndDateTimeframe"
                :list="endDateTimeframeOptions"
                :placeholder="$t('Not sure')"
                data-test="job-length-dropdown"
                name="job-length-dropdown"
                can-deselect
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            v-if="showEndDate"
            :label="$t('Finish date')"
            :helper-text="$t('When would this job finish?')"
          >
            <div class="tw-w-full">
              <InputDatetimePicker
                v-model="selectedEndDate"
                float-label
                :placeholder="$t('YYYY-MM-DD')"
                name="date_end"
                format="YYYY-MM-DD"
                :min-date="store.form.startDate"
                data-test="job-end-date"
                :error="getErrorMessage(validationStore, 'endDate')"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Extension')"
            :helper-text="$t('Is there a possibility to extend or renew this contract?')"
            optional
          >
            <WdsToggle v-model:checked="isExtensionAvailable" data-test="job-contract-extension" />
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Recruitment')"
            :helper-text="$t('When do you expect to review and reply to applicants?')"
          >
            <div class="tw-w-full">
              <WdsSelect
                v-model="selectedEvaluationPeriod"
                :list="evaluationPeriodOptions"
                :placeholder="$t('Unknown')"
                :error="getErrorMessage(validationStore, 'evaluationPeriod')"
                data-test="job-evaluation-period-dropdown"
                name="job-evaluation-period-dropdown"
              />
            </div>
          </BaseFieldGroup>

          <BaseFieldGroup
            :label="$t('Submission deadline')"
            :helper-text="
              $t('The date and time by which candidates should be submitted for this job.')
            "
            optional
          >
            <WdsStack direction-column gap-md>
              <WdsRadioGroup
                v-if="showSourcingDeadlineChoice"
                :checked="sourcingDeadlineUiChoice"
                name="job-sourcing-deadline-choice"
                column
                @update:checked="onSourcingDeadlineChoiceChange(String($event))"
              >
                <WdsRadio value="use_default" data-test="job-sourcing-deadline-choice-use-default">
                  <WdsText
                    :text="$t('Use default')"
                    :sub-text="`${$t('Will be set to')} ${companyDefaultSourcingDeadlineHours} ${$t('hours after this job becomes available.')}`"
                  />
                </WdsRadio>
                <WdsRadio value="none" data-test="job-sourcing-deadline-choice-none">
                  {{ $t('No deadline') }}
                </WdsRadio>
                <WdsRadio value="custom" data-test="job-sourcing-deadline-choice-custom">
                  {{ $t('Custom') }}
                </WdsRadio>
              </WdsRadioGroup>

              <WdsStack
                v-if="!showSourcingDeadlineChoice || sourcingDeadlineUiChoice === 'custom'"
                gap-md
              >
                <div class="tw-w-full">
                  <WdsInputDate
                    v-model="sourcingEndDateDate"
                    format="yyyy-mm-dd"
                    :placeholder="$t('YYYY-MM-DD')"
                    data-test="job-sourcing-end-date"
                    :error="getErrorMessage(validationStore, 'sourcingEndDate')"
                  />
                </div>
                <div class="tw-w-full">
                  <WdsInputTime v-model="sourcingEndDateTime" data-test="job-sourcing-end-time" />
                </div>
              </WdsStack>
            </WdsStack>
          </BaseFieldGroup>
        </BaseSectionBody>
      </BaseSection>

      <WdsAlert info>
        <template #default>
          {{ $t('Summary') }}
        </template>

        <template #description>
          {{ availabilitySummary }}
        </template>
      </WdsAlert>
    </WdsStack>

    <Location />
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import {
  WdsAlert,
  WdsInputDate,
  WdsInputTime,
  WdsRadio,
  WdsRadioGroup,
  WdsSelect,
  WdsStack,
  WdsText,
  WdsToggle,
} from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import InputDatetimePicker from '@/components/InputDatetimePicker.vue'
import BaseSection from '@/components/BaseSection.vue'
import BaseSectionHeader from '@/components/BaseSectionHeader.vue'
import BaseSectionBody from '@/components/BaseSectionBody.vue'
import BaseFieldGroup from '@/components/BaseFieldGroup.vue'
import { JobStartTimeframe, JobEndTimeframe, SourcingDeadlineChoice } from '@/gql/graphql'

import useGetSummaryText from '@/components/Job/composables/getSummaryText'
import { getErrorMessage } from '@/components/Job/functions/getErrorMessage'
import { useJobFormStore } from '@/components/Job/Edit/store/jobFormStore'
import { useValidationStore } from '@/components/Job/Edit/store/useValidationStore'
import Location from '@/components/Job/Edit/Tabs/DetailsTab/Location.vue'
import {
  associationOptions,
  endDateTimeframeOptions,
  evaluationPeriodOptions,
  startDateTimeframeOptions,
} from '@/components/Job/constants'

const store = useJobFormStore()
const validationStore = useValidationStore()

const showStartDate = computed(() => store.form.startDateTimeframe === JobStartTimeframe.OnDate)
const showEndDate = computed(() => store.form.endDateTimeframe === JobEndTimeframe.OnDate)

const isExtensionAvailable = computed({
  get: () => store.form.isExtensionAvailable,
  set: (checked) => {
    store.form.isExtensionAvailable = checked
  },
})

const selectedAssociation = computed({
  get: () => ({ value: store.form.commitment }),
  set: (selected) => {
    store.form.commitment = selected?.value
  },
})

const selectedEvaluationPeriod = computed({
  get: () => ({ value: store.form.evaluationPeriod }),
  set: (selected) => {
    store.form.evaluationPeriod = selected.value
  },
})

const companyDefaultSourcingDeadlineHours = inject<number | null>(
  'companyDefaultSourcingDeadlineHours',
  null,
)

const pad = (n: number) => String(n).padStart(2, '0')

const sourcingEndDateDate = computed({
  get: () => store.form.sourcingEndDateLocal?.slice(0, 10) ?? '',
  set: (date: string) => {
    if (date === '') {
      store.form.sourcingEndDateLocal = null
      return
    }
    const time = store.form.sourcingEndDateLocal?.slice(11, 16) || '09:00'
    store.form.sourcingEndDateLocal = `${date}T${time}:00`
  },
})

const sourcingEndDateTime = computed({
  get: () => store.form.sourcingEndDateLocal?.slice(11, 16) ?? '',
  set: (time: string) => {
    if (time === '') return
    const now = new Date()
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    const date = store.form.sourcingEndDateLocal?.slice(0, 10) || today
    store.form.sourcingEndDateLocal = `${date}T${time}:00`
  },
})

const sourcingDeadlineUiChoice = ref('use_default')

const deadlineChoiceFromStore = () =>
  store.form.sourcingDeadlineChoice === SourcingDeadlineChoice.None
    ? 'none'
    : store.form.sourcingEndDateLocal
      ? 'custom'
      : 'use_default'

// Display-only sync after async job hydration — must not write to the store.
watch(
  () => store.persistedJob,
  (job) => {
    if (job) {
      sourcingDeadlineUiChoice.value = deadlineChoiceFromStore()
    }
  },
  { immediate: true },
)

const showSourcingDeadlineChoice = computed(
  () =>
    // Draft only — `is_available` is true before publish.
    !store.persistedJob?.published && companyDefaultSourcingDeadlineHours !== null,
)

function onSourcingDeadlineChoiceChange(choice: string) {
  sourcingDeadlineUiChoice.value = choice
  if (choice === 'use_default') {
    store.form.sourcingDeadlineChoice = SourcingDeadlineChoice.UseDefault
    store.form.sourcingEndDateLocal = null
  } else if (choice === 'none') {
    store.form.sourcingDeadlineChoice = SourcingDeadlineChoice.None
    store.form.sourcingEndDateLocal = null
  } else {
    store.form.sourcingDeadlineChoice = null
  }
}

const selectedStartDateTimeframe = computed({
  get: () => ({ value: store.form.startDateTimeframe }),
  set: (selected) => {
    store.form.startDateTimeframe = selected.value
  },
})

const selectedEndDateTimeframe = computed({
  get: () => ({ value: store.form.endDateTimeframe }),
  set: (selected) => {
    store.form.endDateTimeframe = selected.value
  },
})

const selectedStartDate = computed({
  get: () => store.form.startDate ?? '',
  set: (date) => {
    store.form.startDate = date === '' ? undefined : date
  },
})

const selectedEndDate = computed({
  get: () => store.form.endDate ?? '',
  set: (date) => {
    store.form.endDate = date === '' ? undefined : date
  },
})

const availabilitySummary = useGetSummaryText()
</script>
